import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { BeatLoader } from "react-spinners";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  StorePriceDisplay,
  StoreMoneyDisplay,
} from "./components/StorePriceDisplay";
import { useStoreAuth } from "./StoreAuthContext";
import { useStoreCart } from "./StoreCartContext";
import { localizedStoreProductName, isStoreProductOutOfStock } from "../../utils/storeUtils";
import "./MyOrders.css";
import "./OrderDetail.css";

const STATUS_CONFIG = {
  delivered: { labelKey: "statusDelivered", color: "#059669" },
  shipped: { labelKey: "statusShipped", color: "#2563eb" },
  confirmed: { labelKey: "statusConfirmed", color: "#ea580c" },
  pending: { labelKey: "statusPending", color: "#d97706" },
  cancelled: { labelKey: "statusCancelled", color: "#6b7280" },
};

const TIMELINE_STEPS = ["pending", "confirmed", "shipped", "delivered"];

function orderStatusForTimeline(status) {
  const s = String(status || "").toLowerCase();
  if (s === "processing") return "confirmed";
  return s;
}

const RETURN_DAYS = 14;

function formatDate(dateStr, lang) {
  if (!dateStr) return "—";
  const locale = String(lang || "").startsWith("ar") ? "ar-EG" : "en-GB";
  return new Date(dateStr).toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatTime(dateStr, lang) {
  if (!dateStr) return "—";
  const locale = String(lang || "").startsWith("ar") ? "ar-EG" : "en-GB";
  return new Date(dateStr).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function OrderDetail() {
  const { t, i18n } = useTranslation("global");
  const tr = React.useCallback(
    (key, defaultValue, opts = {}) =>
      t(`store.orderDetail.${key}`, { defaultValue, ...opts }),
    [t]
  );
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const orderPlaced = location.state?.orderPlaced === true;
  const { isAuthenticated, loading: authLoading } = useStoreAuth();
  const { setCart } = useStoreCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [returnSubmitting, setReturnSubmitting] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [returnForm, setReturnForm] = useState({ type: "return", reason: "", imageUrl: "" });
  const [returnImageUploading, setReturnImageUploading] = useState(false);
  const [productsList, setProductsList] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [showReturnPolicy, setShowReturnPolicy] = useState(false);
  const [reviewStates, setReviewStates] = useState({});
  const [payRetrying, setPayRetrying] = useState(false);
  const returnImageInputRef = React.useRef(null);

  const canPayNow =
    order &&
    order.payment?.method === "online" &&
    order.payment?.status !== "paid" &&
    (order.status || "").toLowerCase() === "pending";

  const handlePayNow = async () => {
    try {
      setPayRetrying(true);
      const { data } = await axios.post(`/payments/tap/retry/${orderId}`);
      if (data?.checkoutUrl) {
        window.location.assign(data.checkoutUrl);
        return;
      }
      toast.error(tr("payNowFailed", "Could not open the payment page. Try again."));
      setPayRetrying(false);
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          tr("payNowFailed", "Could not open the payment page. Try again.")
      );
      setPayRetrying(false);
    }
  };

  // Tap redirects back with ?payment=failed when the charge wasn't captured.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("payment") === "failed") {
      toast.error(
        tr("paymentFailedToast", "Payment was not completed. You can try again below.")
      );
      navigate(location.pathname, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated || !orderId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`/orders/${orderId}`)
      .then(({ data }) => {
        if (!cancelled) setOrder(data);
      })
      .catch((err) => {
        if (!cancelled) {
          if (err.response?.status === 403 || err.response?.status === 404) {
            navigate("/store/my-orders", { replace: true });
          } else {
            toast.error(tr("toastLoadFailed", "Failed to load order."));
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isAuthenticated, orderId, navigate]);

  useEffect(() => {
    if (orderPlaced) {
      toast.success(tr("toastOrderPlaced", "Order placed successfully! We'll contact you soon."));
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [orderPlaced]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!order?.items?.length) return;
    let cancelled = false;
    axios.get("/store/products").then(({ data }) => {
      if (!cancelled && Array.isArray(data)) setProductsList(data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [order?._id, order?.items?.length]);

  const canShowReturnSection = () => {
    if (!order || order.status !== "delivered") return false;
    const deliveredAt = order.tracking?.deliveredAt || order.updatedAt;
    const cutoff = new Date(deliveredAt);
    cutoff.setDate(cutoff.getDate() + RETURN_DAYS);
    return new Date() <= cutoff;
  };

  // Paid orders are cancellable (with automatic refund) only within 24h of
  // payment — after that the server rejects the cancel, so hide the button.
  const REFUND_WINDOW_MS = 24 * 60 * 60 * 1000;
  const isPaid = order?.payment?.status === "paid";
  const paidAtMs = order?.payment?.paidAt ? new Date(order.payment.paidAt).getTime() : NaN;
  const pastRefundWindow =
    isPaid && (!Number.isFinite(paidAtMs) || Date.now() - paidAtMs > REFUND_WINDOW_MS);
  const canCancel =
    order &&
    ["pending", "confirmed"].includes((order.status || "").toLowerCase()) &&
    !pastRefundWindow;

  const outOfStockCount = order?.items?.length && productsList.length > 0
    ? order.items.filter((item) => {
        const p = productsList.find((pr) => String(pr._id) === String(item.productId));
        return p && isStoreProductOutOfStock(p);
      }).length
    : 0;
  const reorderDisabled = outOfStockCount === (order?.items?.length || 0) && outOfStockCount > 0;

  const handleCancelOrderConfirm = () => {
    if (!order?._id || !canCancel || cancelSubmitting) return;
    setShowCancelModal(false);
    setCancelSubmitting(true);
    axios
      .post(`/orders/${order._id}/cancel`)
      .then(({ data }) => {
        toast.success(
          data?.refunded
            ? tr(
                "toastOrderCancelledRefunded",
                "Order cancelled. Your payment will be refunded to your card."
              )
            : tr("toastOrderCancelled", "Order cancelled.")
        );
        setOrder((o) =>
          o
            ? {
                ...o,
                status: "cancelled",
                payment: data?.refunded
                  ? { ...o.payment, status: "refunded" }
                  : o.payment,
              }
            : o
        );
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || tr("toastCancelFailed", "Failed to cancel order."));
      })
      .finally(() => setCancelSubmitting(false));
  };

  const handleReorder = () => {
    if (!order?.items?.length || reorderDisabled) return;
    setShowReorderModal(false);
    const inStockItems = order.items.filter((item) => {
      const p = productsList.find((pr) => String(pr._id) === String(item.productId));
      return !p || !isStoreProductOutOfStock(p);
    }).map((item) => ({
      _id: item.productId,
      name: item.name,
      arName: item.arName,
      image: { url: item.imageUrl || "" },
      price: item.price,
      quantity: item.quantity,
      selectedColor: item.selectedColor || undefined,
      selectedColorHex: item.selectedColorHex || undefined,
    }));
    const notAvailableCount = (order.items?.length || 0) - inStockItems.length;
    setCart((prev) => {
      const merged = [...prev];
      inStockItems.forEach((item) => {
        const existing = merged.find(
          (i) =>
            i._id === item._id &&
            (i.selectedColor || "") === (item.selectedColor || "")
        );
        if (existing) {
          existing.quantity = (existing.quantity || 0) + (item.quantity || 0);
        } else {
          merged.push({ ...item, quantity: item.quantity || 1 });
        }
      });
      return merged;
    });
    if (notAvailableCount > 0) {
      toast.success(
        tr("toastReorderPartial", "{{added}} item(s) added to cart. {{missing}} item(s) not available (out of stock).", {
          added: inStockItems.length,
          missing: notAvailableCount,
        }),
        { autoClose: 5000 }
      );
    } else {
      toast.success(tr("toastItemsAdded", "Items added to cart."));
    }
    navigate("/store/checkout");
  };

  useEffect(() => {
    if (!order || order.status !== "delivered" || !order.items?.length) return;
    let cancelled = false;
    const checks = order.items.map((item) =>
      axios
        .get(`/store/product-reviews/check?orderId=${order._id}&productId=${item.productId}`)
        .then(({ data }) => ({ productId: String(item.productId), reviewed: data.reviewed, existingReview: data.review }))
        .catch(() => ({ productId: String(item.productId), reviewed: false, existingReview: null }))
    );
    Promise.all(checks).then((results) => {
      if (cancelled) return;
      const states = {};
      results.forEach(({ productId, reviewed, existingReview }) => {
        states[productId] = { rating: existingReview?.rating || 0, comment: existingReview?.comment || "", submitting: false, done: reviewed, existingReview };
      });
      setReviewStates(states);
    });
    return () => { cancelled = true; };
  }, [order?._id, order?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReviewSubmit = async (productId) => {
    const state = reviewStates[productId] || {};
    if (!state.rating || state.submitting || state.done) return;
    setReviewStates((prev) => ({ ...prev, [productId]: { ...prev[productId], submitting: true } }));
    try {
      await axios.post("/store/product-reviews", {
        orderId: order._id,
        productId,
        rating: state.rating,
        comment: (state.comment || "").trim(),
      });
      setReviewStates((prev) => ({ ...prev, [productId]: { ...prev[productId], submitting: false, done: true } }));
      toast.success(tr("toastReviewSubmitted", "Review submitted. Thank you!"));
    } catch (err) {
      const msg = err.response?.data?.error || tr("toastReviewFailed", "Failed to submit review.");
      toast.error(msg);
      setReviewStates((prev) => ({ ...prev, [productId]: { ...prev[productId], submitting: false } }));
    }
  };

  const handleReturnImageFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setReturnImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const { data } = await axios.post("/upload/return-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) setReturnForm((f) => ({ ...f, imageUrl: data.url }));
    } catch {
      toast.error(tr("toastUploadFailed", "Image upload failed. You can paste an image URL instead."));
    } finally {
      setReturnImageUploading(false);
    }
  };

  const handleReturnSubmit = (e) => {
    e.preventDefault();
    if (!order?._id || !returnForm.reason.trim()) {
      toast.error(tr("toastEnterReason", "Please enter a reason."));
      return;
    }
    setReturnSubmitting(true);
    axios
      .post(`/orders/${order._id}/return-request`, {
        type: returnForm.type,
        reason: returnForm.reason.trim(),
        imageUrl: returnForm.imageUrl || undefined,
      })
      .then(() => {
        toast.success(tr("toastRequestSubmitted", "Request submitted. We'll get back to you soon."));
        setReturnForm({ type: "return", reason: "", imageUrl: "" });
        axios.get(`/orders/${orderId}`).then(({ data }) => setOrder(data));
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || tr("toastRequestFailed", "Failed to submit request."));
      })
      .finally(() => setReturnSubmitting(false));
  };

  if (!authLoading && !isAuthenticated) {
    navigate("/store/my-orders", { replace: true });
    return null;
  }

  if (authLoading || loading || !order) {
    return (
      <div className="store-page store-order-detail-page">
        <div className="store-my-orders-loader">
          <BeatLoader color="#2a2520" size={12} />
        </div>
      </div>
    );
  }

  const rawOrderStatus = (order.status || "pending").toLowerCase();
  const statusKey = rawOrderStatus === "processing" ? "confirmed" : rawOrderStatus;
  const statusLabel = STATUS_CONFIG[statusKey]
    ? tr(STATUS_CONFIG[statusKey].labelKey, statusKey)
    : statusKey;
  const statusColor = STATUS_CONFIG[statusKey]?.color || "#6b7280";
  const currentStepIndex = TIMELINE_STEPS.indexOf(orderStatusForTimeline(order.status));
  const isDelivered = order.status === "delivered";
  const tracking = order.tracking || {};

  return (
    <section className="store-page store-order-detail-page">
      <div className="store-order-detail-inner container">
        <Link to="/store/my-orders" className="store-order-detail-back">
          {tr("backToMyOrders", "← Back to My Orders")}
        </Link>

        <div className="store-order-detail-header">
          <h1 className="store-order-detail-title">
            {tr("orderNumber", "Order #{{id}}", {
              id: String(order._id).slice(-8).toUpperCase(),
            })}
          </h1>
          <span
            className="store-order-detail-status-badge"
            style={{ backgroundColor: statusColor, color: "#fff" }}
          >
            {statusLabel}
          </span>
        </div>

        <div className="store-order-detail-actions-top">
          {outOfStockCount > 0 && (
            <p className="store-order-detail-out-of-stock-msg">
              {tr("outOfStockMsg", "{{count}} item(s) currently out of stock. You can still add the rest to cart.", { count: outOfStockCount })}
            </p>
          )}
          <button
            type="button"
            onClick={() => setShowReorderModal(true)}
            disabled={reorderDisabled}
            title={
              reorderDisabled
                ? tr("reorderDisabledTitle", "All items are out of stock")
                : tr("reorderEnabledTitle", "Add available items to cart and go to checkout")
            }
            className="store-my-orders-btn store-my-orders-btn-outline store-my-orders-btn-sm"
          >
            {tr("reorder", "Reorder")}
          </button>
          {canCancel && (
            <button
              type="button"
              onClick={() => setShowCancelModal(true)}
              disabled={cancelSubmitting}
              className="store-my-orders-btn store-order-detail-cancel-btn store-my-orders-btn-sm"
            >
              {cancelSubmitting ? tr("cancelling", "Cancelling…") : tr("cancelOrder", "Cancel order")}
            </button>
          )}
          {pastRefundWindow &&
            ["pending", "confirmed"].includes((order?.status || "").toLowerCase()) && (
              <p className="store-order-detail-cancel-window-note">
                {tr(
                  "cancelWindowClosed",
                  "Orders can no longer be cancelled after 24 hours from payment."
                )}
              </p>
            )}
        </div>

        {showCancelModal && (
          <div className="store-order-detail-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="cancel-modal-title">
            <div className="store-order-detail-modal">
              <h3 id="cancel-modal-title" className="store-order-detail-modal-title">{tr("cancelModalTitle", "Cancel order?")}</h3>
              <p className="store-order-detail-modal-text">
                {isPaid
                  ? tr(
                      "cancelModalTextRefund",
                      "This action cannot be undone. Your order will be cancelled and the amount you paid will be refunded to your card."
                    )
                  : tr("cancelModalText", "This action cannot be undone. Your order will be cancelled.")}
              </p>
              <div className="store-order-detail-modal-actions">
                <button type="button" className="store-my-orders-btn store-order-detail-cancel-btn store-my-orders-btn-sm" onClick={() => setShowCancelModal(false)}>{tr("keepOrder", "Keep order")}</button>
                <button type="button" className="store-my-orders-btn store-my-orders-btn-primary store-my-orders-btn-sm" onClick={handleCancelOrderConfirm}>{tr("confirmCancel", "Yes, cancel order")}</button>
              </div>
            </div>
          </div>
        )}

        {showReorderModal && (
          <div className="store-order-detail-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="reorder-modal-title">
            <div className="store-order-detail-modal">
              <h3 id="reorder-modal-title" className="store-order-detail-modal-title">{tr("reorderModalTitle", "Reorder?")}</h3>
              <p className="store-order-detail-modal-text">
                {outOfStockCount > 0
                  ? tr("reorderModalPartial", "Only in-stock items ({{available}}) will be added to cart. {{unavailable}} item(s) are out of stock.", {
                      available: (order?.items?.length || 0) - outOfStockCount,
                      unavailable: outOfStockCount,
                    })
                  : tr("reorderModalAll", "All items from this order will be added to your cart and you will be taken to checkout.")}
              </p>
              <div className="store-order-detail-modal-actions">
                <button type="button" className="store-my-orders-btn store-my-orders-btn-secondary store-my-orders-btn-sm" onClick={() => setShowReorderModal(false)}>{tr("cancel", "Cancel")}</button>
                <button type="button" className="store-my-orders-btn store-my-orders-btn-primary store-my-orders-btn-sm" onClick={handleReorder}>{tr("addToCartCheckout", "Add to cart & checkout")}</button>
              </div>
            </div>
          </div>
        )}

        <div className="store-order-detail-grid">
          <div className="store-order-detail-main">
            <section className="store-order-detail-block">
              <h2 className="store-order-detail-block-title">{tr("itemsTitle", "Items")}</h2>
              <ul className="store-order-detail-items">
                {(order.items || []).map((item, i) => (
                  <li key={i} className="store-order-detail-item">
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt=""
                        className="store-order-detail-item-img"
                      />
                    ) : (
                      <div className="store-order-detail-item-placeholder" />
                    )}
                    <div className="store-order-detail-item-info">
                      <span className="store-order-detail-item-name">
                        {localizedStoreProductName(item, i18n.language)}
                      </span>
                      {item.sku ? (
                        <span className="store-order-detail-item-sku" dir="ltr">
                          {t("store.productSerial", { defaultValue: "Product code" })}: {item.sku}
                        </span>
                      ) : null}
                      {item.selectedColor && (
                        <span className="store-order-detail-item-meta">
                          {tr("colorLabel", "Color: {{color}}", { color: item.selectedColor })}
                        </span>
                      )}
                      <span
                        className="store-order-detail-item-meta"
                        dir="ltr"
                      >
                        {t("store.orderLineQtyTimes", {
                          qty: item.quantity,
                          defaultValue: "{{qty}} ×",
                        })}{" "}
                        <StorePriceDisplay amount={item.price || 0} />
                      </span>
                    </div>
                    <span className="store-order-detail-item-total">
                      <StoreMoneyDisplay
                        amount={item.quantity * (item.price || 0)}
                        currencyCode={order.totals?.currency || "SAR"}
                      />
                    </span>
                  </li>
                ))}
              </ul>
              <div className="store-order-detail-totals">
                <div className="store-order-detail-totals-row">
                  <span>{tr("subtotal", "Subtotal")}</span>
                  <span>
                    <StoreMoneyDisplay
                      amount={order.totals?.subtotal ?? 0}
                      currencyCode={order.totals?.currency || "SAR"}
                    />
                  </span>
                </div>
                {Number(order.totals?.shipping ?? 0) > 0 && (
                  <div className="store-order-detail-totals-row">
                    <span>{tr("shipping", "Shipping")}</span>
                    <span>
                      <StoreMoneyDisplay
                        amount={order.totals.shipping}
                        currencyCode={order.totals.currency || "SAR"}
                      />
                    </span>
                  </div>
                )}
                <div className="store-order-detail-totals-row store-order-detail-totals-total">
                  <span>{tr("total", "Total")}</span>
                  <span>
                    <StoreMoneyDisplay
                      amount={order.totals?.total ?? 0}
                      currencyCode={order.totals?.currency || "SAR"}
                    />
                  </span>
                </div>
              </div>
            </section>

            <section className="store-order-detail-block">
              <h2 className="store-order-detail-block-title">{tr("deliveryAddress", "Delivery address")}</h2>
              <p className="store-order-detail-address">
                <bdi>{order.customer?.fullName}</bdi>
                <br />
                <bdi>{order.customer?.address}</bdi>
                <br />
                <bdi dir="ltr">{order.customer?.phone}</bdi>
                {order.customer?.email ? (
                  <>
                    {" • "}
                    <bdi dir="ltr">{order.customer.email}</bdi>
                  </>
                ) : null}
              </p>
            </section>

            <section className="store-order-detail-block">
              <h2 className="store-order-detail-block-title">{tr("payment", "Payment")}</h2>
              <p className="store-order-detail-payment">
                {order.payment?.method === "cod"
                  ? tr("paymentCod", "Cash on delivery")
                  : tr("paymentOnline", "Online")}
                {order.payment?.status
                  ? ` (${tr(
                      `payStatus${order.payment.status.charAt(0).toUpperCase()}${order.payment.status.slice(1)}`,
                      order.payment.status
                    )})`
                  : ""}
              </p>
              {canPayNow && (
                <div className="store-order-detail-pay-now">
                  <p className="store-order-detail-pay-now-hint">
                    {tr(
                      "payNowHint",
                      "This order is not paid yet. Complete the payment to confirm it."
                    )}
                  </p>
                  <button
                    type="button"
                    className="store-order-detail-pay-now-btn"
                    onClick={handlePayNow}
                    disabled={payRetrying}
                  >
                    {payRetrying
                      ? tr("payNowLoading", "Opening payment page...")
                      : tr("payNow", "Pay now")}
                  </button>
                </div>
              )}
            </section>

            <section className="store-order-detail-block" id="timeline">
              <h2 className="store-order-detail-block-title">{tr("orderTimeline", "Order timeline")}</h2>
              {order.updatedAt && (
                <p className="store-order-detail-last-updated">
                  {tr("lastUpdatedAt", "Last updated at {{time}}", { time: formatTime(order.updatedAt, i18n.language) })}
                </p>
              )}
              <div className="store-order-detail-timeline">
                {TIMELINE_STEPS.map((step, i) => {
                  const done = currentStepIndex >= i;
                  const label =
                    step === "pending"
                      ? tr("stepPlaced", "Order placed")
                      : tr(
                          `status${step.charAt(0).toUpperCase()}${step.slice(1)}`,
                          step.charAt(0).toUpperCase() + step.slice(1)
                        );
                  return (
                    <div
                      key={step}
                      className={`store-order-detail-timeline-step ${done ? "done" : ""}`}
                    >
                      <div className="store-order-detail-timeline-dot" />
                      <div className="store-order-detail-timeline-label">{label}</div>
                      {i < TIMELINE_STEPS.length - 1 && (
                        <div className="store-order-detail-timeline-line" />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="store-order-detail-block" id="track">
              <h2 className="store-order-detail-block-title">{tr("trackOrder", "Track order")}</h2>
              {order.updatedAt && (
                <p className="store-order-detail-last-updated">
                  {tr("lastUpdatedAt", "Last updated at {{time}}", { time: formatTime(order.updatedAt, i18n.language) })}
                </p>
              )}
              {isDelivered ? (
                <div className="store-order-detail-track-delivered">
                  <p className="store-order-detail-track-confirm">{tr("statusDelivered", "Delivered")}</p>
                  {tracking.deliveredAt && (
                    <p className="store-order-detail-track-date">
                      {tr("deliveredOn", "Delivered on {{date}}", { date: formatDate(tracking.deliveredAt, i18n.language) })}
                    </p>
                  )}
                </div>
              ) : (
                <div className="store-order-detail-track-active">
                  {tracking.shippingCompany && (
                    <p><strong>{tr("shippingCompany", "Shipping company:")}</strong> {tracking.shippingCompany}</p>
                  )}
                  {tracking.trackingNumber && (
                    <p><strong>{tr("trackingNumber", "Tracking number:")}</strong> <bdi dir="ltr">{tracking.trackingNumber}</bdi></p>
                  )}
                  {tracking.estimatedDeliveryDate && (
                    <p><strong>{tr("estimatedDelivery", "Estimated delivery:")}</strong> {formatDate(tracking.estimatedDeliveryDate, i18n.language)}</p>
                  )}
                  {!tracking.shippingCompany && !tracking.trackingNumber && (
                    <p className="store-order-detail-track-pending">
                      {tr("trackingPending", "Tracking info will appear here once your order is shipped.")}
                    </p>
                  )}
                </div>
              )}
            </section>

            {isDelivered && (order.items || []).length > 0 && (
              <section className="store-order-detail-block" id="reviews">
                <h2 className="store-order-detail-block-title">{tr("rateProducts", "Rate your products")}</h2>
                <p className="store-order-detail-review-intro">{tr("reviewIntro", "Share your experience to help other shoppers.")}</p>
                <ul className="store-order-detail-review-list">
                  {(order.items || []).map((item, i) => {
                    const pid = String(item.productId);
                    const rs = reviewStates[pid] || {};
                    const alreadyDone = rs.done;
                    return (
                      <li key={i} className="store-order-detail-review-item">
                        <div className="store-order-detail-review-item-header">
                          {item.imageUrl && (
                            <img src={item.imageUrl} alt="" className="store-order-detail-review-thumb" />
                          )}
                          <span className="store-order-detail-review-item-name">
                            {localizedStoreProductName(item, i18n.language)}
                          </span>
                        </div>
                        {alreadyDone ? (
                          <div className="store-order-detail-review-done">
                            <div className="store-order-detail-review-stars">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <span key={s} className={`store-order-detail-star ${s <= rs.rating ? "is-filled" : ""}`}>★</span>
                              ))}
                            </div>
                            {rs.comment && <p className="store-order-detail-review-done-comment">"{rs.comment}"</p>}
                            <p className="store-order-detail-review-done-label">{tr("reviewDone", "Review submitted ✓")}</p>
                          </div>
                        ) : (
                          <div className="store-order-detail-review-form">
                            <div className="store-order-detail-review-stars">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <button
                                  key={s}
                                  type="button"
                                  aria-label={`${s} star${s !== 1 ? "s" : ""}`}
                                  className={`store-order-detail-star store-order-detail-star-btn ${s <= (rs.rating || 0) ? "is-filled" : ""}`}
                                  onClick={() => setReviewStates((prev) => ({ ...prev, [pid]: { ...prev[pid], rating: s } }))}
                                >★</button>
                              ))}
                            </div>
                            <textarea
                              className="store-order-detail-review-textarea"
                              placeholder={tr("reviewPlaceholder", "Write a comment (optional)")}
                              rows={3}
                              value={rs.comment || ""}
                              onChange={(e) => setReviewStates((prev) => ({ ...prev, [pid]: { ...prev[pid], comment: e.target.value } }))}
                            />
                            <button
                              type="button"
                              disabled={!rs.rating || rs.submitting}
                              className="store-my-orders-btn store-my-orders-btn-primary"
                              onClick={() => handleReviewSubmit(pid)}
                            >
                              {rs.submitting ? tr("submitting", "Submitting…") : tr("submitReview", "Submit review")}
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {canShowReturnSection() && (
              <section className="store-order-detail-block" id="return">
                <div className="store-order-detail-block-title-row">
                  <h2 className="store-order-detail-block-title">{tr("returnExchange", "Return & exchange")}</h2>
                  <span
                    className="store-order-detail-return-info-icon"
                    title={tr("returnIntro", "You can request a return or exchange within {{days}} days of delivery.", { days: RETURN_DAYS })}
                    aria-label={tr("returnIntro", "You can request a return or exchange within {{days}} days of delivery.", { days: RETURN_DAYS })}
                  >
                    <i className="fa-solid fa-circle-info" aria-hidden />
                  </span>
                </div>
                <p className="store-order-detail-return-intro">
                  {tr("returnIntro", "You can request a return or exchange within {{days}} days of delivery.", { days: RETURN_DAYS })}
                </p>
                <div className="store-order-detail-return-policy-wrap">
                  <button type="button" className="store-order-detail-return-policy-toggle" onClick={() => setShowReturnPolicy((v) => !v)} aria-expanded={showReturnPolicy}>
                    {showReturnPolicy ? tr("hideReturnPolicy", "Hide return policy") : tr("viewReturnPolicy", "View return policy")}
                  </button>
                  {showReturnPolicy && (
                    <div className="store-order-detail-return-policy">
                      <p>{tr("returnPolicyText", "Items must be unused and in original packaging. Refunds are processed within 5–10 business days after we receive the item. Exchanges are subject to availability.")}</p>
                    </div>
                  )}
                </div>
                {(order.returnRequests || []).length > 0 && (
                  <div className="store-order-detail-return-list">
                    <p className="store-order-detail-return-list-title">{tr("yourRequests", "Your requests")}</p>
                    {order.returnRequests.map((req, i) => (
                      <div key={i} className="store-order-detail-return-request">
                        <span>
                          {req.type === "return" ? tr("returnWord", "Return") : tr("exchangeWord", "Exchange")} —{" "}
                          <strong
                            className={
                              req.status === "approved"
                                ? "status-approved"
                                : req.status === "rejected"
                                ? "status-rejected"
                                : "status-pending"
                            }
                          >
                            {req.status === "approved"
                              ? tr("reqStatusApproved", "approved")
                              : req.status === "rejected"
                              ? tr("reqStatusRejected", "rejected")
                              : tr("reqStatusPending", "pending")}
                          </strong>
                        </span>
                        <span className="store-order-detail-return-reason">{req.reason}</span>
                      </div>
                    ))}
                  </div>
                )}
                <form onSubmit={handleReturnSubmit} className="store-order-detail-return-form">
                  <label className="store-order-detail-return-label">
                    {tr("requestType", "Request type")}
                    <select
                      value={returnForm.type}
                      onChange={(e) => setReturnForm((f) => ({ ...f, type: e.target.value }))}
                      className="store-order-detail-return-select"
                    >
                      <option value="return">{tr("requestReturn", "Request return")}</option>
                      <option value="exchange">{tr("requestExchange", "Request exchange")}</option>
                    </select>
                  </label>
                  <label className="store-order-detail-return-label">
                    {tr("reasonLabel", "Reason")}
                    <textarea
                      value={returnForm.reason}
                      onChange={(e) => setReturnForm((f) => ({ ...f, reason: e.target.value }))}
                      placeholder={tr("reasonPlaceholder", "Describe the reason for your request")}
                      rows={3}
                      className="store-order-detail-return-textarea"
                      required
                    />
                  </label>
                  <label className="store-order-detail-return-label">
                    {tr("imageOptional", "Image (optional)")}
                    <div className="store-order-detail-return-image-upload">
                      <input
                        ref={returnImageInputRef}
                        type="file"
                        accept="image/*"
                        className="store-order-detail-return-file-input"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleReturnImageFile(file);
                          e.target.value = "";
                        }}
                        aria-label="Upload image"
                      />
                      <div
                        className="store-order-detail-return-dropzone"
                        onClick={() => returnImageInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("is-dragover"); }}
                        onDragLeave={(e) => e.currentTarget.classList.remove("is-dragover")}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove("is-dragover");
                          const file = e.dataTransfer.files?.[0];
                          if (file && file.type.startsWith("image/")) handleReturnImageFile(file);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); returnImageInputRef.current?.click(); } }}
                      >
                        {returnImageUploading
                          ? tr("uploading", "Uploading…")
                          : returnForm.imageUrl
                          ? tr("imageAdded", "Image added ✓")
                          : tr("dropImage", "Drop image here or click to upload")}
                      </div>
                      <input
                        type="url"
                        value={returnForm.imageUrl}
                        onChange={(e) => setReturnForm((f) => ({ ...f, imageUrl: e.target.value }))}
                        placeholder={tr("pasteImageUrl", "Or paste image URL")}
                        className="store-order-detail-return-input"
                      />
                    </div>
                  </label>
                  <button
                    type="submit"
                    disabled={returnSubmitting}
                    className="store-my-orders-btn store-my-orders-btn-primary"
                  >
                    {returnSubmitting ? tr("submitting", "Submitting…") : tr("submitRequest", "Submit request")}
                  </button>
                </form>
              </section>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
